import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { EmergencyCall } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCalls.types';
import emergencyCalls from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import transcriptions, {
  TranscriptionStateToken,
  TranscriptionStateKeywordSetMatch,
} from '../transcriptionSlice';

// Mock token that will be used across all stories
export const mockToken: TranscriptionStateToken = {
  token_id: 'token1',
  start_time: new Date('2024-04-18T18:20:00.000Z').valueOf(),
  end_time: new Date('2024-04-18T18:20:10.000Z').valueOf(),
  speaker_id: 'speaker1',
  speaker_name: 'Dispatcher',
  speaker_role: 'Dispatcher',
  speaker_display_name: 'Dispatcher (Position 15)',
  transcript:
    'There is an emergency situation at the downtown shopping center with multiple people requiring medical assistance.',
  detected_language: 'en',
  translated_transcript: {
    es: 'Hay una situación de emergencia en el centro comercial del centro con varias personas que requieren asistencia médica.',
  },
  translated:
    'Hay una situación de emergencia en el centro comercial del centro con varias personas que requieren asistencia médica.',
};

// patterns for mocking redux loosely based on https://redux.js.org/usage/writing-tests
const rootReducer = combineReducers({
  emergencyCalls,
  transcriptions,
});

type RootState = ReturnType<typeof rootReducer>;

function setupStore(preloadedState?: Partial<RootState>) {
  return configureStore({
    reducer: rootReducer,
    preloadedState,
  });
}

export function createMockStoreWithKeywords(
  keywordMatches: TranscriptionStateKeywordSetMatch[] = [],
  selectedKeywordMatch: {
    keywordSetID: string;
    matchIndex: number;
  } | null = null,
) {
  const selectedCallerId = 'test-caller-id';

  return setupStore({
    emergencyCalls: {
      selectedCallerID: selectedCallerId,
      callQueue: {
        [selectedCallerId]: {
          core: {
            locations: [
              {
                call_start_time: 0,
              },
            ],
            secondaryLocations: [],
          },
        } as unknown as EmergencyCall,
      },
      numberWithNewLocation: null,
      locationType: 'call',
      loading: {
        shareEmergencyCall: false,
      },
      removedCalls: [],
    },
    transcriptions: {
      selectedTranscript: 'original',
      autoSelectedTranscript: false,
      transcriptionLists: {},
      transcriptionTotals: {},
      calls: {
        [selectedCallerId]: {
          keyword_matches: keywordMatches,
          selectedKeywordMatch,
          fetching: false,
          error: null,
          payload: {
            tokens: [mockToken],
            speakers: [
              {
                display_name: 'Dispatcher',
                name: 'dispatcher',
                role: 'dispatcher',
                id: 'speaker1',
              },
            ],
            call_information: {
              caller_id: 'mock-caller-id',
              digi_call_id: 'mock-digi-call-id',
              call_start_time: 0,
              call_end_time: 1000,
            },
            languagesDetected: ['en'],
            audio_data: {
              status: 'pending',
            },
          },
          summaries: [],
          keywords: [],
        },
      },
      maximized: false,
    },
  });
}

// No highlights scenario
export const emptyStore = createMockStoreWithKeywords();

// Single word highlight scenario
export const singleWordHighlightStore = createMockStoreWithKeywords([
  {
    keyword_set: {
      name: 'Emergency Terms',
      id: '9a96d259-94f0-4419-8adf-404a054f7c3d',
    },
    matches: [
      {
        token_id: 'token1',
        keyword: 'emergency',
        match_index: 12,
        match_length: 9,
      },
    ],
  },
]);

// Multiple words highlight scenario
export const multipleWordsHighlightStore = createMockStoreWithKeywords([
  {
    keyword_set: {
      name: 'Emergency Terms',
      id: '9a96d259-94f0-4419-8adf-404a054f7c3d',
    },
    matches: [
      {
        token_id: 'token1',
        keyword: 'emergency',
        match_index: 12,
        match_length: 9,
      },
      {
        token_id: 'token1',
        keyword: 'medical',
        match_index: 95,
        match_length: 7,
      },
    ],
  },
]);

// Translated text highlight scenario
export const translatedTextHighlightStore = createMockStoreWithKeywords([
  {
    keyword_set: {
      name: 'Emergency Terms - Spanish',
      id: '9a96d259-94f0-4419-8adf-404a054f7c3d',
    },
    matches: [
      {
        token_id: 'token1',
        keyword: 'emergencia',
        match_index: 21,
        match_length: 10,
        language: 'es',
      },
    ],
  },
]);
